<template>
  <div class="page-admin-go-nogo">
    <BaseLoadingBar is-small v-if="pending" />

    <div class="page-admin-go-nogo__body">
      <!-- Phones: its main action, as the "+" of the settings header. -->
      <PageMainAction :label="$t('page.admin-go-nogo.add-item')" @click="openAddItem" />
      <div v-if="!isPhone" class="page-admin-go-nogo__table-header-actions">
        <div class="page-admin__count">
          <span class="number-monospace">{{ opportunityVoteReasons ? opportunityVoteReasons.length : 0 }}</span>
          {{ ' ' + $tc('basic-words.item', opportunityVoteReasons.length) }}
        </div>
        <div class="page-admin__actions">
          <BaseRefreshButton :size="40" :pending="pending" @refresh="onRefresh" />
          <BaseButton @click.native="openAddItem" has-icon-left>
            <BaseIcon name="plus" />
            {{ $t('page.admin-go-nogo.add-item') }}
          </BaseButton>
        </div>
      </div>

      <div class="page-admin-go-nogo__table">
        <TableNormal
          v-sortable
          :headers="headers"
          :items="opportunityVoteReasons"
          :items-per-page="itemsToShow"
          :fixed-header="true"
          :single-select="false"
          hide-default-footer
          item-key="id"
          :mobile-breakpoint="620"
          @click:row="openEditItem"
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ opportunityVoteReasons ? opportunityVoteReasons.length : 0 }}</span>
            {{ ' ' + $tc('basic-words.item', opportunityVoteReasons.length) }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="onRefresh" />
          </template>
          <template v-slot:item.reason="{ item }">
            <BaseChip size="sm" preset="soft" :tone="getGoNoGoReasonTone(item.voteType)" :interactive="false">
              {{ item.reason }}
            </BaseChip>
          </template>
          <template v-slot:item.actions="{ item }">
            <BaseMoreActions
              v-if="isPhone"
              :title="item.reason"
              :actions="rowActions(item)"
              @select="onRowAction($event, item)"
            />
            <v-menu v-else>
              <template v-slot:activator="{ on, attrs }">
                <div @click.stop v-on="on" v-bind="attrs" class="menuAction">
                  <BaseIcon name="more-horizontal" class="page-admin-go-nogo__more" />
                </div>
              </template>
              <div class="page-admin-go-nogo__actions-menu base-actions-menu">
                <ul>
                  <li @click="openEditItem(item)">
                    <BaseIcon :size="44" name="edit-2" />
                    {{ $t('actions.edit') }}
                  </li>
                  <li v-if="item && !item.isReserved" class="delete" @click="openDeleteItem(item)">
                    <BaseIcon :size="44" name="trash-2" />
                    {{ $t('actions.delete') }}
                  </li>
                </ul>
              </div>
            </v-menu>
          </template>
        </TableNormal>
      </div>
    </div>

    <DeleteGoNoGoItem ref="deleteGoNoGoItem" />
    <EditAddGoNoGoItem ref="editAddGoNoGoItem" />
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseChip from '@/components/base/BaseChip.vue';
import { getGoNoGoReasonTone } from '@/util/tag-tones/go-nogo-reason-tag';
import TableNormal from '@/components/table/TableNormal.vue';
import EditAddGoNoGoItem from '@/components/page-go-nogo/EditAddGoNoGoItemDialog.vue';
import DeleteGoNoGoItem from '@/components/page-go-nogo/DeleteGoNoGoItemDialog.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import { mapActions, mapGetters } from 'vuex';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import { viewport } from '@/util/viewport';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';

export default {
  name: 'PageAdminGoNoGo',
  components: {
    BaseMoreActions,
    BaseRefreshButton,
    PageMainAction,
    BaseButton,
    BaseIcon,
    BaseLoadingBar,
    BaseChip,
    TableNormal,
    EditAddGoNoGoItem,
    DeleteGoNoGoItem,
  },
  data() {
    return {
      itemsToShow: 100,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('goNoGo', ['opportunityVoteReasons', 'pending']),
    headers() {
      return [
        {
          text: this.$t('page.admin-go-nogo.reason'),
          sortable: true,
          value: 'reason',
        },
        {
          sortable: false,
          value: 'actions',
          width: '70px',
        },
      ];
    },
  },
  methods: {
    // The row's ⋯ on phones, a sheet (BaseMoreActions); the desktop's menu lists the same.
    rowActions(item) {
      return [
        { key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' },
        ...(item.isReserved
          ? []
          : [{ key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true }]),
      ];
    },
    onRowAction(key, item) {
      if (key === 'edit') this.openEditItem(item);
      else this.openDeleteItem(item);
    },
    ...mapActions('goNoGo', ['getOpportunityVoteReasons']),
    openAddItem() {
      const itemData = {
        voteType: '',
        reason: '',
      };
      this.$refs.editAddGoNoGoItem.openDialog(itemData);
    },
    openEditItem(item) {
      const itemData = {
        id: item.id,
        voteType: item.voteType,
        reason: item.reason,
      };
      this.$refs.editAddGoNoGoItem.openDialog(itemData);
    },
    openDeleteItem(item) {
      this.$refs.deleteGoNoGoItem.openDialog(item);
    },
    async onRefresh() {
      await this.getOpportunityVoteReasons();
    },
    getGoNoGoReasonTone,
  },
  async created() {
    await this.getOpportunityVoteReasons();
  },
};
</script>

<style lang="scss">
.page-admin-go-nogo {
  &__body {
    padding: 32px;
  }
  &__table-header-actions {
    padding-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__table {
    margin-bottom: 32px;
  }
  &__actions-menu {
    cursor: pointer;
    background-color: $color-white;
  }
}
</style>
